// @flow

import * as React from 'react';
import { css, keyframes } from '@emotion/react';
import { Flex, Box } from 'react-system';
import { useConstant } from '@realadvisor/hooks';
import { useSpring } from '@realadvisor/spring';
import { useTheme } from '../theme';
import {
  requestAnimationTimeout,
  cancelAnimationTimeout,
} from '../utils/animation-timeout.js';

import { scrollToElement } from '../utils/scrollToElement';
import { getOffsetTop } from '../utils/getOffsetTop';

const scrollToBubble = node => {
  // manually 32 0 looks good
  const bottomOffset = 32;
  const offsetTop = getOffsetTop(node);
  if (
    offsetTop + node.offsetHeight - (window.scrollY || window.pageYOffset) >
    window.innerHeight - bottomOffset
  ) {
    scrollToElement(
      node,
      window.innerHeight - node.offsetHeight - bottomOffset,
    );
  }
};

const Bubble = ({
  animate,
  delay,
  children,
}: {|
  animate: boolean,
  delay: number,
  children: React.Node,
|}): React.Node => {
  const containerRef = React.useRef<HTMLElement | null>(null);
  const bubbleRef = React.useRef<HTMLElement | null>(null);
  const [animationComplete, setAnimationComplete] = React.useState(
    animate === false,
  );
  const { set } = useSpring({
    from: animationComplete
      ? { opacity: 1, y: 0, scale: 1 }
      : { opacity: 0, y: 300, scale: 1 },
    tension: 300,
    friction: 25,
    onUpdate: ({ opacity, y, scale }) => {
      if (bubbleRef.current) {
        bubbleRef.current.style.opacity = `${opacity}`;
        bubbleRef.current.style.transform = `translate(0, ${y}px) scale(${scale})`;
      }
    },
  });
  // start the animation
  React.useEffect(() => {
    if (bubbleRef.current && containerRef.current && animate) {
      const container = containerRef.current;
      const bubble = bubbleRef.current;
      const timeoutId = requestAnimationTimeout(delay, () => {
        scrollToBubble(container);
        set({ opacity: 1, y: 0, scale: 1 }, () => {
          // Remove `transform` property when it's back to 0.
          // Having a transform forces Chrome to create a compositing layer
          // which causes a z-index bug with dropdowns:
          // https://user-images.githubusercontent.com/45102199/53961919-d7eac600-40fa-11e9-950b-ce62e04c20ae.png
          // Reproducible at least in Chromium Version 72.0.3626.121
          bubble.style.transform = '';
          setAnimationComplete(true);
        });
      });
      return () => {
        cancelAnimationTimeout(timeoutId);
      };
    }
  }, [animate, delay, set]);
  return (
    // aria-hidden allow e2e tests to not touch the element until animation is finished
    <div ref={containerRef} aria-hidden={animationComplete ? false : true}>
      <div ref={bubbleRef} style={{ opacity: 0 }}>
        {children}
      </div>
    </div>
  );
};

export const BubblesSeq = ({
  animate = true,
  children,
  skipCount = 0,
}: {|
  animate?: boolean,
  children: React.Node,
  skipCount?: number,
|}): React.Node => {
  const initialAnimate = useConstant(() => animate);
  const result = React.Children.toArray(children)
    .filter(Boolean)
    .map((element, index) => {
      const realIndex = skipCount + index;
      if (element.type === BubbleLoading) {
        const delay = element.props.delay ?? 500;
        return (
          <BubbleLoading
            key={realIndex}
            delay={delay + realIndex * 1000}
            animate={initialAnimate}
          />
        );
      } else {
        return (
          <Bubble
            key={realIndex}
            animate={initialAnimate}
            delay={500 + realIndex * 1000}
          >
            {element}
          </Bubble>
        );
      }
    });
  return result;
};

export const BubbleBox = ({
  children,
}: {|
  children: React.Node,
|}): React.Node => {
  const { text } = useTheme();
  return (
    <Box py={2}>
      <Box
        p={3}
        css={[
          text.body1,
          {
            background: 'rgb(227, 242, 253)',
            borderRadius: '8px 8px 8px 0',
          },
        ]}
      >
        {children}
      </Box>
    </Box>
  );
};

const loading = keyframes`
  0%,
  80%,
  100% {
    box-shadow: 0 2.5em 0 -1.3em;
  }
  40% {
    box-shadow: 0 2.5em 0 0;
  }
`;

export const BubbleLoadingAnim = ({
  className,
}: {|
  className?: number,
  css?: any,
|}): React.Node => {
  const duration = 1.2;
  const color = '#9e9e9e';
  const opacity = 0.5;
  const size = 6;
  return (
    <div
      className={className}
      css={css`
        animation: ${duration}s infinite ease-in-out;
        animation-name: ${loading};
        animation-delay: ${duration * -0.16}s;
        animation-fill-mode: both;
        border-radius: 50%;
        color: ${color};
        opacity: ${opacity};
        font-size: ${size}px;
        height: 2.5em;
        margin: 0px auto 30px auto;
        position: relative;
        text-indent: -9999em;
        width: 2.5em;
        &:before {
          animation: ${duration}s infinite ease-in-out;
          animation-name: ${loading};
          animation-delay: ${duration * -0.32}s;
          animation-fill-mode: both;
          border-radius: 50%;
          content: '';
          height: 2.5em;
          left: -3.5em;
          position: absolute;
          top: 0;
          width: 2.5em;
        }
        &:after {
          animation: ${duration}s infinite ease-in-out;
          animation-name: ${loading};
          animation-fill-mode: both;
          border-radius: 50%;
          content: '';
          height: 2.5em;
          left: 3.5em;
          position: absolute;
          top: 0;
          width: 2.5em;
        }
      `}
    ></div>
  );
};

export const BubbleLoading = ({
  delay,
  animate,
}: {|
  delay?: number,
  animate?: boolean,
|}): React.Node => {
  const ref = React.useRef<HTMLElement | null>(null);
  const [visibility, setVisibility] = React.useState('hidden');
  React.useEffect(() => {
    if (animate === true && delay != null) {
      let animationId = null;
      const delayId = requestAnimationTimeout(delay, () => {
        const { current } = ref;
        if (current != null) {
          scrollToBubble(current);
        }
        setVisibility('visible');
        animationId = requestAnimationTimeout(800, () => {
          setVisibility('hidden');
        });
      });
      return () => {
        cancelAnimationTimeout(delayId);
        cancelAnimationTimeout(animationId);
      };
    }
  }, [animate, delay]);
  if (delay == null || animate == null || animate === false) {
    return null;
  }
  return (
    <div css={{ position: 'relative' }} style={{ visibility }}>
      <div
        ref={ref}
        css={{
          position: 'absolute',
          top: 0,
          right: 0,
          left: 0,
        }}
      >
        <Flex p={2} alignItems={'center'}>
          <BubbleLoadingAnim />
        </Flex>
      </div>
    </div>
  );
};
